import { createGoogleGenerativeAI } from '@ai-sdk/google';
import { generateObject } from 'ai';
import { useConfigStore } from '@stores/v4/useConfigStore';
import { groupLogsByExercise, extractHighNum } from '@/shared/logic/performance-logic';
import { MetabolicService } from '@/services/metabolic.service';
import { BurnEstimationSchema, AIPrompts } from '@/shared/logic/ai-logic';

/**
 * AI WORKOUT SERVICE (v4)
 * Hybrid Engine: Grounded by Physics, Refined by AI.
 * REFACTORED: Decoupled schemas and prompts.
 */

const getProvider = () => {
    const apiKey = useConfigStore.getState().getActiveKey();
    if (!apiKey) console.error("[AI-Workout] CRITICAL: Active API Key is missing.");
    
    return createGoogleGenerativeAI({
        apiKey: apiKey || 'MISSING_KEY',
    });
};

export const AIWorkoutService = {
    async estimateSessionBurn(sessionLogs: any[], profile: any, durationMinutes: number) {
        try {
            const google = getProvider();
            const weightKg = extractHighNum(profile?.last_weight) || 85;
            
            // 1. GROUP THE LOGS
            const grouped = groupLogsByExercise(sessionLogs);
            
            // 2. CALCULATE PHYSICS BASELINE
            let totalTonnage = 0;
            let totalDistanceKm = 0;
            let avgIntensity = 0;
            let logCount = 0;
            let isStrength = false;

            const exerciseSummary = grouped.map(g => {
                const tempo = g.sets[0]?.block_items?.tempo || 'STANDARD';
                return {
                    exercise: g.name,
                    tempo: tempo,
                    sets: g.sets.map((s: any) => {
                        const dist = (s.distance_meters || 0) / 1000;
                        const duration = s.duration_seconds || 0;
                        
                        if (dist > 0 || duration > 0) {
                            totalDistanceKm += dist;
                        } else {
                            totalTonnage += (extractHighNum(s.weight) * (s.reps || 0));
                            isStrength = true;
                        }
                        
                        avgIntensity += (s.rpe || 7);
                        logCount++;

                        return s.duration_seconds > 0 
                            ? `Set ${s.set_number}: ${s.duration_seconds}s | ${s.distance_meters}m | Intensity: ${s.rpe}%`
                            : `Set ${s.set_number}: ${s.weight}kg x ${s.reps} | RPE: ${s.rpe}`;
                    })
                };
            });

            avgIntensity = logCount > 0 ? (avgIntensity / logCount) : 50;

            const physicsBaseline = isStrength 
                ? MetabolicService.calculateStrengthBaseline(totalTonnage, weightKg, durationMinutes, avgIntensity / 7)
                : MetabolicService.calculateCardioBaseline(weightKg, durationMinutes, totalDistanceKm, avgIntensity);

            // 3. AI REFINEMENT
            const { object } = await generateObject({
                model: google('gemini-2.5-flash-lite'),
                schema: BurnEstimationSchema,
                prompt: AIPrompts.workoutRefinement(physicsBaseline, weightKg, profile, durationMinutes, exerciseSummary)
            });

            return { data: object, error: null };
        } catch (err: any) {
            console.error("[AI-Workout] Estimation FAILED:", err);
            return { data: null, error: err.message };
        }
    }
};
